import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import Swal from 'sweetalert2';
import { finalize } from 'rxjs';

import { LoginService } from '../auth/login.service';
import { DeviceDetectorService } from '../../../services/device-detector.service';

interface LoginResponse {
  token: string;
user?: {
  id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  phone?: string;
  email?: string;

  type?: number | string;
  user_type?: 'candidate' | 'admin' | 'company';

  company_id?: number | null;
  doctor_id?: number | null;

  user_cv?: string | null;
  user_cv_url?: string | null;
  cv?: string | null;

  access_pages?: string[];
  has_access_to_phone_numbers?: boolean;
};
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  person!: FormGroup;

  contentReady = false;
  loggedIn = false;
  isMobile = false;
  isAr = false;
  submitted = false;
  isPasswordVisible = false;

  private isBrowser: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private loginService: LoginService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.createForm();

    if (!this.isBrowser) {
      return;
    }

    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = this.getLocalStorageItem('language') === 'ar';

    const token = this.loginService.getToken();

    if (token) {
      this.loggedIn = true;
      this.router.navigate(['/Home']);
    }
  }

  private createForm(): void {
    this.person = this.formBuilder.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
  }

  private getLocalStorageItem(key: string): string | null {
    if (!this.isBrowser) {
      return null;
    }

    return window.localStorage.getItem(key);
  }

  private setLocalStorageItem(key: string, value: string): void {
    if (!this.isBrowser) {
      return;
    }

    window.localStorage.setItem(key, value);
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  isInvalid(controlName: string): boolean {
    const control = this.person.get(controlName);

    return !!(
      control &&
      control.invalid &&
      (control.touched || control.dirty || this.submitted)
    );
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.person.invalid) {
      this.person.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'كمّل البيانات المطلوبة' : 'Complete required fields',
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      });

      return;
    }

    this.contentReady = true;

    this.loginService
      .user_login(this.person.value)
      .pipe(finalize(() => (this.contentReady = false)))
      .subscribe({
next: (res: LoginResponse) => {
  if (!res?.token) {
    Swal.fire({
      icon: 'error',
      title: this.isAr ? 'حصل خطأ في تسجيل الدخول' : 'Login error',
      text: this.isAr
        ? 'التوكن غير موجود في الرد'
        : 'Token was not returned from server',
      confirmButtonText: this.isAr ? 'تمام' : 'OK',
    });

    return;
  }

  const userType = res.user?.user_type;
  const legacyType = res.user?.type !== undefined && res.user?.type !== null
    ? String(res.user.type)
    : null;

  // ✅ امنع الأدمن من الدخول هنا
  // لو الأدمن عندك type = 1
  const isAdmin = userType === 'admin' || legacyType === '1';

  if (isAdmin) {
    if (this.isBrowser) {
      window.localStorage.removeItem('token');
      window.localStorage.removeItem('username');
      window.localStorage.removeItem('user_type');
      window.localStorage.removeItem('user');
      window.localStorage.removeItem('user_cv');
      window.localStorage.removeItem('user_cv_url');

    }

    Swal.fire({
      icon: 'warning',
      title: this.isAr
        ? 'غير مسموح للأدمن بالدخول من هنا'
        : 'Admin login is not allowed here',
      text: this.isAr
        ? 'من فضلك استخدم لوحة تحكم الأدمن'
        : 'Please use the admin dashboard login',
      confirmButtonText: this.isAr ? 'تمام' : 'OK',
    });

    return;
  }

  if (res.user?.username) {
    this.setLocalStorageItem('username', res.user.username);
  }
if (res.user?.id !== undefined && res.user?.id !== null) {
  this.setLocalStorageItem('user_id', String(res.user.id));
}

if (res.user?.first_name) {
  this.setLocalStorageItem('first_name', res.user.first_name);
}

if (res.user?.last_name) {
  this.setLocalStorageItem('last_name', res.user.last_name);
}

if (res.user?.phone) {
  this.setLocalStorageItem('phone', res.user.phone);
}

if (res.user?.email) {
  this.setLocalStorageItem('email', res.user.email);
}

  if (res.user?.user_type) {
    this.setLocalStorageItem('user_type', res.user.user_type);
  }

  if (res.user?.type !== undefined && res.user?.type !== null) {
    this.setLocalStorageItem('type', String(res.user.type));
  }

  if (res.user?.company_id !== undefined && res.user?.company_id !== null) {
    this.setLocalStorageItem('company_id', String(res.user.company_id));
  } else if (this.isBrowser) {
    window.localStorage.removeItem('company_id');
  }

  if (res.user?.doctor_id !== undefined && res.user?.doctor_id !== null) {
    this.setLocalStorageItem('doctor_id', String(res.user.doctor_id));
  } else if (this.isBrowser) {
    window.localStorage.removeItem('doctor_id');
  }

  if (res.user?.access_pages) {
    this.setLocalStorageItem(
      'access_pages',
      JSON.stringify(res.user.access_pages)
    );
  }

  this.setLocalStorageItem(
    'has_access_to_phone_numbers',
    String(!!res.user?.has_access_to_phone_numbers)
  );

  this.saveUserData(res.user);
  this.loginService.setToken(res.token);

  Swal.fire({
    icon: 'success',
    title: this.isAr
      ? 'أهلاً بيك في أحمد الشحات'
      : 'Welcome to Ahmed El-Shahat',
    showConfirmButton: false,
    timer: 1500,
  });

  this.router.navigate(['/Home']);
},

        error: () => {
          Swal.fire({
            icon: 'error',
            title: this.isAr
              ? 'البيانات اللي مدخلها فيها حاجة غلط'
              : 'Wrong username or password',
            text: this.isAr
              ? 'لو المشكلة مستمرة كلم الدعم'
              : 'If the problem continues, please contact support',
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });
        },
      });
  }

  private saveUserData(user: LoginResponse['user']): void {
    if (!this.isBrowser || !user) {
      return;
    }

    const cv = user.user_cv || user.user_cv_url || user.cv || '';

    this.setLocalStorageItem('user', JSON.stringify(user));

    if (cv) {
      this.setLocalStorageItem('user_cv', cv);
      this.setLocalStorageItem('user_cv_url', cv);
    } else {
      window.localStorage.removeItem('user_cv');
      window.localStorage.removeItem('user_cv_url');
    }
  }
}
